import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, MousePointer2 } from 'lucide-react';

/**
 * SwipeCarousel — independent, swipeable, snap-to-slide carousel.
 *
 * Requirements satisfied (FEAT-007 + FEAT-008):
 *   - One full-width slide per item (every embed/article fully visible)
 *   - Thin linear progress bar + Prev/Next arrows. NO dot indicators.
 *   - Smooth drag/swipe via mouse and touch
 *   - Snap to nearest slide on release; fast flick advances (momentum)
 *   - If only one item: render it directly with no carousel chrome
 *   - Each instance is fully isolated — interacting with one does not
 *     affect any other carousel on the page
 *
 * Iframe-friendly desktop dragging:
 *   YouTube / SoundCloud / Spotify iframes capture all mouse events so the
 *   parent's mousedown never fires — making a plain swipe carousel
 *   un-draggable on PC. We solve this with an absolutely-positioned
 *   transparent "drag overlay" on top of the active slide. The overlay
 *   captures mousedown for drag detection. If the user releases without
 *   dragging, we treat it as a click and either (a) forward the click to
 *   the element below (for <a> and <button>) or (b) self-disable the
 *   overlay so the next click reaches the iframe (for iframes). Resets
 *   automatically when the active slide changes.
 *
 * Props:
 *   items     — array of anything; renderItem(item, index) returns the slide
 *   testId    — e.g. "youtube" produces data-testid="carousel-youtube"
 *   ariaLabel — optional, for screen readers
 *   header    — optional ReactNode rendered above the carousel (icon + label)
 */
const SwipeCarousel = ({ items = [], renderItem, testId, ariaLabel, header }) => {
  const n = items.length;
  const [active, setActive] = useState(0);
  const [drag, setDrag] = useState(0);
  const [overlayDisabled, setOverlayDisabled] = useState(false);
  const trackRef = useRef(null);
  const widthRef = useRef(0);
  const startXRef = useRef(null);
  const startYRef = useRef(null);
  const startTRef = useRef(0);
  const movedRef = useRef(false);
  const wasDragRef = useRef(false);
  const mouseDownRef = useRef(false);

  // Swipe-hint nudge — shown once per testId per session, after the carousel
  // scrolls into view. Auto-dismisses on first user interaction or after the
  // animation completes.
  const [showHint, setShowHint] = useState(false);
  const hintShownRef = useRef(false);
  const containerRef = useRef(null);
  const HINT_KEY = `swipe-hint-shown-${testId || 'default'}`;

  useEffect(() => {
    if (n <= 1) return undefined;
    if (typeof window === 'undefined') return undefined;
    try {
      if (sessionStorage.getItem(HINT_KEY)) return undefined;
    } catch {
      /* sessionStorage unavailable — show hint anyway */
    }
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hintShownRef.current) {
            hintShownRef.current = true;
            // Tiny delay so the hint feels intentional, not jarring
            setTimeout(() => setShowHint(true), 350);
            try { sessionStorage.setItem(HINT_KEY, '1'); } catch { /* ignore */ }
            // Auto-clear after the animation finishes
            setTimeout(() => setShowHint(false), 350 + 1900);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [n, HINT_KEY]);

  const dismissHint = () => {
    if (showHint) setShowHint(false);
  };

  // Recompute width on mount + on resize
  useEffect(() => {
    const measure = () => {
      if (trackRef.current) widthRef.current = trackRef.current.offsetWidth || 0;
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Clamp active when items array shrinks
  useEffect(() => {
    if (active >= n && n > 0) setActive(0);
  }, [n, active]);

  // When the active slide changes, re-arm the drag overlay so the new slide
  // is once again swipeable. (Without this, after the user clicks on slide 1
  // and the overlay disables, switching to slide 2 would leave it disabled.)
  useEffect(() => {
    setOverlayDisabled(false);
  }, [active]);

  const goTo = useCallback(
    (idx) => {
      if (n === 0) return;
      const next = ((idx % n) + n) % n;
      setActive(next);
      setDrag(0);
    },
    [n],
  );

  const goNext = useCallback(() => { dismissHint(); goTo(active + 1); }, [active, goTo]);
  const goPrev = useCallback(() => { dismissHint(); goTo(active - 1); }, [active, goTo]);

  // ---- Drag / swipe handlers ----
  const onPointerStart = (clientX, clientY) => {
    dismissHint();
    startXRef.current = clientX;
    startYRef.current = clientY;
    startTRef.current = Date.now();
    movedRef.current = false;
    wasDragRef.current = false;
  };

  const onPointerMove = (clientX, clientY, e) => {
    if (startXRef.current === null) return;
    const dx = clientX - startXRef.current;
    const dy = clientY - (startYRef.current ?? clientY);
    if (!movedRef.current && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      movedRef.current = true;
    }
    if (movedRef.current) {
      const cap = widthRef.current || 1;
      const capped = Math.max(-cap * 1.05, Math.min(cap * 1.05, dx));
      setDrag(capped);
      if (e?.preventDefault) e.preventDefault();
    }
  };

  const onPointerEnd = (clientX) => {
    if (startXRef.current === null) return;
    const dx = clientX !== undefined ? clientX - startXRef.current : drag;
    const dt = Math.max(1, Date.now() - startTRef.current);
    const velocity = dx / dt;
    const w = widthRef.current || 1;

    let target = active;
    if (Math.abs(velocity) > 0.6 && Math.abs(dx) > 20) {
      target = velocity < 0 ? active + 1 : active - 1;
    } else if (Math.abs(dx) > w * 0.18) {
      target = dx < 0 ? active + 1 : active - 1;
    }
    wasDragRef.current = movedRef.current;
    startXRef.current = null;
    startYRef.current = null;
    movedRef.current = false;
    goTo(target);
  };

  // Touch
  const onTouchStart = (e) => {
    const t = e.touches[0];
    onPointerStart(t.clientX, t.clientY);
  };
  const onTouchMove = (e) => {
    const t = e.touches[0];
    onPointerMove(t.clientX, t.clientY, e);
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    onPointerEnd(t.clientX);
  };

  // Mouse — listened on the drag overlay (always) AND on the track itself
  // (works for non-iframe slides where pointer events reach the track).
  const onMouseDown = (e) => {
    mouseDownRef.current = true;
    onPointerStart(e.clientX, e.clientY);
  };
  const onMouseMove = (e) => {
    if (!mouseDownRef.current) return;
    onPointerMove(e.clientX, e.clientY);
  };
  const onMouseUp = (e) => {
    if (!mouseDownRef.current) return;
    mouseDownRef.current = false;
    onPointerEnd(e.clientX);
  };
  const onMouseLeave = (e) => {
    if (mouseDownRef.current) onMouseUp(e);
  };

  // When the user releases without dragging, the overlay's onClick decides
  // whether to forward the click to the underlying content or to self-disable
  // (for iframes, where synthetic events can't penetrate the cross-origin frame).
  const onOverlayClick = (e) => {
    if (wasDragRef.current) {
      wasDragRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const overlay = e.currentTarget;
    if (!overlay) return;
    overlay.style.pointerEvents = 'none';
    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (target && target.tagName === 'IFRAME') {
      // Cross-origin iframe — leave overlay off so the user's next click
      // reaches the iframe natively. Re-arms on slide change.
      setOverlayDisabled(true);
    } else if (target && target !== overlay) {
      // Native links / buttons / cards — forward the click and re-arm.
      try {
        target.click();
      } catch {
        /* ignore */
      }
      requestAnimationFrame(() => {
        if (overlay && overlay.style) overlay.style.pointerEvents = 'auto';
      });
    } else {
      requestAnimationFrame(() => {
        if (overlay && overlay.style) overlay.style.pointerEvents = 'auto';
      });
    }
  };

  // Per-slide track translate
  const translate = useMemo(() => {
    const w = widthRef.current || 0;
    return `translate3d(${-active * w + drag}px, 0, 0)`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, drag, n]);

  if (n === 0) return null;

  // Single-item: render directly with no carousel chrome (FEAT-007 / FEAT-008)
  if (n === 1) {
    return (
      <div data-testid={`carousel-${testId}`} aria-label={ariaLabel}>
        {header}
        {renderItem(items[0], 0)}
      </div>
    );
  }

  const progressPct = ((active + 1) / n) * 100;

  return (
    <div
      ref={containerRef}
      data-testid={`carousel-${testId}`}
      aria-label={ariaLabel}
      className="select-none"
    >
      {header}
      <div
        ref={trackRef}
        className="overflow-hidden touch-pan-y relative"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <div
          className="flex"
          style={{
            transform: translate,
            transition: drag === 0 ? 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
            willChange: 'transform',
          }}
        >
          {items.map((item, i) => (
            <div
              key={item?.id || item?.book_id || item?.key || `${testId}-${i}`}
              className="shrink-0"
              style={{ width: '100%', minWidth: '100%' }}
              data-testid={`carousel-${testId}-slide-${i}`}
              aria-hidden={i !== active}
            >
              {/* Disable inner pointer events while user is dragging the wrapper */}
              <div style={{ pointerEvents: drag !== 0 ? 'none' : 'auto' }}>
                {renderItem(item, i)}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop drag overlay — only renders on screens >= md so touch
            devices keep their native iframe interactions intact. Sits over
            the active slide so iframe-bearing slides become draggable. */}
        {!overlayDisabled && (
          <div
            className="hidden md:block absolute inset-0 cursor-grab active:cursor-grabbing"
            style={{ background: 'transparent', zIndex: 5 }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            onClick={onOverlayClick}
            data-testid={`carousel-${testId}-drag-overlay`}
            aria-hidden="true"
          />
        )}

        {/* One-time swipe hint — fades a cursor icon drifting right→left to
            teach desktop users that the slides can be dragged. Auto-dismisses
            on first interaction or after the animation. Stored per-testId in
            sessionStorage so it appears at most once per visit. */}
        {showHint && (
          <div
            className="absolute inset-0 pointer-events-none flex items-center justify-center"
            style={{ zIndex: 6 }}
            data-testid={`carousel-${testId}-swipe-hint`}
            aria-hidden="true"
          >
            <div className="absolute top-1/2 right-1/2 animate-swipe-hint">
              <div className="flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-white/20 rounded-full pl-3 pr-4 py-2 shadow-2xl">
                <MousePointer2 className="h-4 w-4 text-white -scale-x-100" />
                <span className="text-xs text-white font-medium whitespace-nowrap">Swipe to browse</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Thin linear progress bar + arrows. NO dots. */}
      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={goPrev}
          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors flex-shrink-0"
          data-testid={`carousel-${testId}-prev`}
          aria-label="Previous"
          type="button"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div
          className="h-1 bg-white/10 rounded-full overflow-hidden flex-1"
          data-testid={`carousel-${testId}-progress`}
        >
          <div
            className="h-full bg-fuchsia-500 transition-all duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="text-xs text-gray-400 tabular-nums w-12 text-right" data-testid={`carousel-${testId}-counter`}>
          {active + 1} / {n}
        </span>
        <button
          onClick={goNext}
          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors flex-shrink-0"
          data-testid={`carousel-${testId}-next`}
          aria-label="Next"
          type="button"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default SwipeCarousel;
